import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, ChevronLeft } from 'lucide-react';
import { useStore } from '../admin/context/ProductStore';
import './Blog.css';

const categories = ['All', 'Health Tips', 'Buying Guide', 'Superfoods', 'Recipes'];

const BlogCard = ({ blog }) => (
  <Link to={`/blog/${blog.slug}`} className="blog-card">
    <div className="blog-img-wrap">
      <img
        src={blog.image}
        alt={blog.title}
        className="blog-img"
        onError={(e) => {
          e.target.src = `https://via.placeholder.com/600x360/fdf8f0/7a4e2d?text=Blog`;
        }}
      />
      <span className="blog-category-badge badge badge-gold">{blog.category}</span>
    </div>
    <div className="blog-body">
      <div className="blog-meta">
        <span>{blog.date}</span>
        <span>·</span>
        <span>{blog.readTime}</span>
      </div>
      <h3 className="blog-title">{blog.title}</h3>
      <p className="blog-excerpt">{blog.excerpt}</p>
      <span className="blog-read-more">
        Read More <ArrowRight size={14} />
      </span>
    </div>
  </Link>
);

export default function Blog() {
  const { slug } = useParams();
  const { blogs: storeBlogs } = useStore();
  const [activeCat, setActiveCat] = useState('All');
  
  const displayBlogs = storeBlogs || [];

  // Filter blogs based on active category
  const filteredBlogs = activeCat === 'All' 
    ? displayBlogs 
    : displayBlogs.filter(b => b.category === activeCat);

  // If we are on a detail page
  if (slug) {
    const post = displayBlogs.find(b => b.slug === slug);
    if (!post) return <div className="container" style={{padding: '100px 0', textAlign: 'center'}}><h2>Post not found</h2><Link to="/blog">Back to Blog</Link></div>;

    return (
      <main className="blog-detail-page">
        <div className="container">
          <Link to="/blog" className="back-to-blog">
            <ChevronLeft size={18} /> Back to Blog
          </Link>
          
          <article className="blog-article">
            <div className="blog-article-header">
              <span className="badge badge-gold">{post.category}</span>
              <h1 className="article-title">{post.title}</h1>
              <div className="article-meta">
                <span><Calendar size={14} /> {post.date}</span>
                <span><Clock size={14} /> {post.readTime}</span>
              </div>
            </div>

            <div className="article-featured-img">
              <img src={post.image} alt={post.title} />
            </div>

            <div className="article-content">
              <p className="article-lead">{post.excerpt}</p>
              <div className="article-body-text">
                {post.content ? (
                  <div dangerouslySetInnerHTML={{ __html: post.content }} />
                ) : (
                  <p>Full content for this article will be available soon. Stay tuned for more nutrition and wellness tips from KOA!</p>
                )}
              </div>
            </div>
          </article>
        </div>
      </main>
    );
  }

  const featured = filteredBlogs[0];
  const rest = filteredBlogs.slice(1);

  return (
    <main className="blog-page">
      {/* Hero */}
      <div className="blog-hero container">
        <span className="eyebrow">KOA Journal</span>
        <h1 className="blog-hero-title">Nutrition, Wellness & <br />Nature's Goodness</h1>
        <p className="blog-hero-sub">Expert tips, healthy recipes, and the latest from the world of nutrition.</p>
      </div>

      {/* Category tabs */}
      <div className="blog-cats-wrap container">
        <div className="blog-cat-tabs">
          {categories.map(cat => (
            <button 
              key={cat} 
              className={`blog-cat-tab ${cat === activeCat ? 'active' : ''}`}
              onClick={() => setActiveCat(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="container blog-content">
        {/* Featured */}
        {featured && activeCat === 'All' && (
          <Link to={`/blog/${featured.slug}`} className="featured-post">
            <div className="featured-img-wrap">
              <img
                src={featured.image}
                alt={featured.title}
                className="featured-img"
                onError={(e) => {
                  e.target.src = `https://via.placeholder.com/900x500/fdf8f0/7a4e2d?text=Featured`;
                }}
              />
              <span className="featured-label badge badge-gold">Featured</span>
            </div>
            <div className="featured-body">
              <span className={`badge badge-green`}>{featured.category}</span>
              <h2 className="featured-title">{featured.title}</h2>
              <p className="featured-excerpt">{featured.excerpt}</p>
              <div className="featured-meta">
                <span>{featured.date}</span>
                <span>·</span>
                <span>{featured.readTime}</span>
                <span className="featured-read-more">
                  Read Article <ArrowRight size={14} />
                </span>
              </div>
            </div>
          </Link>
        )}

        {/* Grid */}
        <div className="blog-grid-page grid grid-3 gap-4">
          {filteredBlogs.length === 0 ? (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '100px 0', opacity: 0.5 }}>
              <h3>No articles in this category.</h3>
              <p>Try exploring another category or check back later!</p>
            </div>
          ) : (
            (activeCat === 'All' ? rest : filteredBlogs).map(blog => (
              <BlogCard key={blog._id || blog.id} blog={blog} />
            ))
          )}
        </div>
      </div>

      {/* Newsletter CTA */}
      <section className="blog-newsletter">
        <div className="container blog-newsletter-inner">
          <div>
            <h3 className="blog-nl-title">Get wellness tips in your inbox</h3>
            <p className="blog-nl-sub">Join 50,000+ health enthusiasts who read our weekly newsletter.</p>
          </div>
          <form className="blog-nl-form" onSubmit={e => e.preventDefault()}>
            <input type="email" placeholder="Your email address" className="blog-nl-input" />
            <button type="submit" className="btn-primary">Subscribe</button>
          </form>
        </div>
      </section>
    </main>
  );
}
